import { DialogHeader, Modal } from '../Modal.js'
import { Badge, Button, type Tone } from '../ui.js'
import type { ReportFinding, ReportFindingGroup } from './types.js'

/**
 * What is wrong with the report, before it goes.
 *
 * Nothing here refuses. The primary button reads "Complete anyway" when there
 * are findings, because a technician in a subfloor with one bar is the wrong
 * person to argue with about a blank field — and the findings are recorded
 * alongside the submission, so nobody can later claim the gaps were invisible.
 *
 * The three groups are in the order they matter. A contradiction is the one that
 * gets a report thrown out in an audit: each answer is defensible alone and the
 * pair is not. So only it is red; vague is "look at this one" and blank is grey.
 */

const GROUPS: Array<{
  group: ReportFindingGroup
  title: string
  tone: Tone
}> = [
  { group: 'CONTRADICTION', title: 'Do not add up', tone: 'red' },
  { group: 'VAGUE', title: 'Too vague for an audit', tone: 'dark' },
  { group: 'BLANK', title: 'Still blank', tone: 'neutral' },
]

export function CheckSheet({
  open,
  onClose,
  findings,
  answered,
  total,
  submitting,
  onJump,
  onComplete,
}: {
  open: boolean
  onClose: () => void
  findings: ReportFinding[]
  answered: number
  total: number
  submitting: boolean
  onJump: (fieldKey: string) => void
  onComplete: () => void
}) {
  const clean = findings.length === 0

  return (
    <Modal open={open} onClose={onClose} labelledBy="check-sheet-title" className="max-w-[520px]">
      <DialogHeader
        id="check-sheet-title"
        title="Check before you send"
        description={
          <>
            <span className="tabular-nums">{answered}</span> of <span className="tabular-nums">{total}</span> filled in
          </>
        }
        onClose={onClose}
      />

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
        {clean ? (
          <div className="rounded-lg bg-brand-50 px-3 py-5 text-center ring-1 ring-brand-200 ring-inset">
            <p className="text-sm font-semibold text-brand-900">Nothing outstanding.</p>
            <p className="mt-1 text-[13px] text-ink-600">
              Every field filled, nothing vague, nothing contradicting itself.
            </p>
          </div>
        ) : null}

        {GROUPS.map(({ group, title, tone }) => {
          const rows = findings.filter((finding) => finding.group === group)
          if (rows.length === 0) return null

          return (
            <section key={group} className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Badge tone={tone} className="font-semibold tabular-nums">
                  {rows.length}
                </Badge>
                <h3 className="text-[13px] font-semibold text-ink-900">{title}</h3>
              </div>

              {rows.map((finding, index) => (
                <button
                  key={`${finding.fieldKey}-${index}`}
                  type="button"
                  onClick={() => onJump(finding.fieldKey)}
                  className="flex flex-col gap-0.5 rounded-lg bg-white px-3 py-2.5 text-left ring-1 ring-ink-200 ring-inset transition-colors hover:bg-ink-50"
                >
                  <span className="text-[13.5px] font-semibold text-ink-900">{finding.title}</span>
                  {finding.detail ? (
                    <span className="text-xs text-ink-500">{finding.detail}</span>
                  ) : null}
                </button>
              ))}
            </section>
          )
        })}
      </div>

      <div className="flex items-center gap-2.5 border-t border-ink-200 px-4 py-3">
        <Button onClick={onClose}>Keep filling in</Button>
        <Button
          variant="primary"
          size="lg"
          block
          loading={submitting}
          onClick={onComplete}
        >
          {clean ? 'Complete the report' : 'Complete anyway'}
        </Button>
      </div>
    </Modal>
  )
}
